import { useCallback, useEffect, useRef, useState } from "react";
import { Timer, ShieldAlert, ChevronLeft, ChevronRight, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useLang } from "@/context/LanguageContext";
import { testApi, errText } from "@/lib/aiot";
import { Mcq, Wire, Rule, Order } from "@/components/aiot/test/TestItems";
import { PlaceRoom } from "@/components/aiot/test/PlaceRoom";

const VIEWS = { mcq: Mcq, wire: Wire, rule: Rule, order: Order, place: PlaceRoom };
const answered = (it, a) => a !== undefined && a !== null && (it.kind !== "wire" || it.pins.every((p) => a[p])) && (it.kind !== "rule" || (a.trigger && a.condition && a.action));

const useFocusGuard = (week, active, onSubmitted, setViolations, flush) => {
  const last = useRef(0);
  useEffect(() => {
    if (!active) return undefined;
    const log = (type) => {
      const t = Date.now();
      if (t - last.current < 2000) return;
      last.current = t;
      flush().then(() => testApi.event(week, type)).then((r) => { setViolations(r.violations); if (r.submitted) onSubmitted(r.result); }).catch(() => {});
    };
    const vis = () => document.visibilityState === "hidden" && log("hidden");
    const blur = () => setTimeout(() => !document.hasFocus() && document.visibilityState === "visible" && log("blur"), 400);
    const fs = () => !document.fullscreenElement && log("fs_exit");
    document.addEventListener("visibilitychange", vis);
    window.addEventListener("blur", blur);
    document.addEventListener("fullscreenchange", fs);
    return () => { document.removeEventListener("visibilitychange", vis); window.removeEventListener("blur", blur); document.removeEventListener("fullscreenchange", fs); };
  }, [week, active, onSubmitted, setViolations, flush]);
};

export const TestRunner = ({ week, title, att, onDone }) => {
  const { L } = useLang();
  const paper = att.paper;
  const [answers, setAnswers] = useState(att.answers || {});
  const [i, setI] = useState(0);
  const [violations, setViolations] = useState(att.violations || 0);
  const [left, setLeft] = useState(0);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const offset = useRef(new Date(att.server_now).getTime() - Date.now());
  const saveT = useRef(null);
  const latest = useRef(att.answers || {});
  const sent = useRef(false);

  const finish = useCallback((result) => { sent.current = true; if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {}); onDone(result); }, [onDone]);
  const submit = useCallback(async (auto) => {
    if (sent.current) return;
    setBusy(true);
    try { finish(await testApi.submit(week, answers)); } catch (e) { toast.error(errText(e, "Could not submit. Retrying…")); setBusy(false); if (auto) setTimeout(() => submit(true), 3000); }
  }, [week, answers, finish]);

  const flush = useCallback(() => { clearTimeout(saveT.current); return testApi.save(week, latest.current); }, [week]);
  useFocusGuard(week, !busy, finish, setViolations, flush);
  useEffect(() => { if (violations > (att.violations || 0)) toast.warning(L(`You left the test window. Warning ${violations} of ${att.max_focus}.`, `आपने टेस्ट विंडो छोड़ी। चेतावनी ${violations} / ${att.max_focus}।`)); }, [violations]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const end = new Date(att.deadline).getTime();
    const tick = () => setLeft(Math.max(0, Math.round((end - (Date.now() + offset.current)) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [att.deadline]);
  useEffect(() => { if (left === 0 && offset.current !== null && new Date(att.deadline).getTime() <= Date.now() + offset.current) submit(true); }, [left]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (n, v) => {
    const next = { ...latest.current, [n]: v };
    latest.current = next;
    setAnswers(next);
    clearTimeout(saveT.current);
    saveT.current = setTimeout(() => testApi.save(week, latest.current), 600);
  };
  const it = paper[i];
  const View = VIEWS[it.kind];
  const done = paper.filter((p) => answered(p, answers[p.n])).length;
  const low = left < 120;

  return (
    <div className="max-w-5xl mx-auto" data-testid="test-runner">
      <div className="sticky top-0 z-20 -mx-4 px-4 py-3 bg-[#F3F8FB]/90 backdrop-blur-xl border-b border-slate-200 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[180px]"><div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F766E]">{L(`Week ${week} test`, `हफ़्ता ${week} टेस्ट`)}</div><div className="text-sm font-extrabold text-[#0B1F3A] truncate">{title}</div></div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3 h-9 text-sm font-bold text-[#B45309]" data-testid="test-violations"><ShieldAlert className="w-4 h-4" />{violations}/{att.max_focus}</span>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-sm font-black tabular-nums ${low ? "bg-[#DC2626] text-white animate-pulse" : "bg-[#0B1F3A] text-white"}`} data-testid="test-timer"><Timer className="w-4 h-4" />{Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</span>
        <button type="button" onClick={() => setConfirm(true)} disabled={busy} className="rounded-full bg-[#0F766E] text-white px-4 h-9 text-sm font-bold inline-flex items-center gap-1.5 hover:bg-[#0B1F3A] disabled:opacity-60" data-testid="test-submit-open">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{L("Submit", "सबमिट")}</button>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5" data-testid="test-navigator">
        {paper.map((p, k) => (
          <button key={p.n} type="button" onClick={() => setI(k)} className={`w-9 h-9 rounded-lg text-xs font-black transition-colors ${k === i ? "bg-[#0B1F3A] text-white" : answered(p, answers[p.n]) ? "bg-[#CCFBF1] text-[#0F766E]" : "bg-white border border-slate-200 text-slate-500"}`} data-testid={`test-nav-${k}`}>{k + 1}</button>
        ))}
      </div>
      <section className="mt-4 rounded-3xl border border-slate-200 bg-white p-5 sm:p-8" data-testid={`test-question-${i}`}>
        <div className="text-xs font-bold text-slate-500">{L(`Question ${i + 1} of ${paper.length}`, `सवाल ${i + 1} / ${paper.length}`)} · {it.points} {L(it.points > 1 ? "points" : "point", "अंक")} · <span data-testid="test-question-topic">{L(it.topic, it.topic_hi)}</span></div>
        <h2 className="mt-2 text-lg sm:text-xl font-extrabold text-[#0B1F3A] leading-snug" data-testid="test-question-text">{it.kind === "mcq" ? L(it.q, it.q_hi) : L(it.ask, it.ask_hi)}</h2>
        <div className="mt-5"><View key={it.n} item={it} value={answers[it.n]} onChange={(v) => set(it.n, v)} /></div>
      </section>
      <div className="mt-4 flex items-center justify-between">
        <button type="button" disabled={i === 0} onClick={() => setI(i - 1)} className="rounded-full border border-slate-300 bg-white px-4 h-10 text-sm font-bold inline-flex items-center gap-1 disabled:opacity-40" data-testid="test-prev"><ChevronLeft className="w-4 h-4" />{L("Previous", "पिछला")}</button>
        <span className="text-xs font-semibold text-slate-500" data-testid="test-answered">{done}/{paper.length} {L("answered", "जवाब दिए")}</span>
        {i < paper.length - 1 ? <button type="button" onClick={() => setI(i + 1)} className="rounded-full bg-[#0B1F3A] text-white px-4 h-10 text-sm font-bold inline-flex items-center gap-1" data-testid="test-next">{L("Next", "अगला")}<ChevronRight className="w-4 h-4" /></button>
          : <button type="button" onClick={() => setConfirm(true)} className="rounded-full bg-[#0F766E] text-white px-4 h-10 text-sm font-bold" data-testid="test-finish">{L("Finish test", "टेस्ट ख़त्म करें")}</button>}
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="test-submit-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{L("Submit your test?", "टेस्ट सबमिट करें?")}</AlertDialogTitle>
            <AlertDialogDescription>{L(`You have answered ${done} of ${paper.length}. There is only one attempt, you cannot change answers after submitting.`, `आपने ${paper.length} में से ${done} के जवाब दिए हैं। सिर्फ़ एक प्रयास है, सबमिट के बाद जवाब नहीं बदल सकते।`)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="test-submit-cancel">{L("Keep working", "जारी रखें")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => submit(false)} className="bg-[#0F766E] hover:bg-[#0B1F3A]" data-testid="test-submit-confirm">{L("Submit now", "अभी सबमिट करें")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
